import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, useToast } from '@caruso/ui';
import { tagsService } from '@/services/tags.service';
import { useAuthStore } from '@/stores/auth-store';
import { Plus, Edit, Trash2, Tag as TagIcon } from 'lucide-react';
import { motion } from 'framer-motion';

export function TagsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN';

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', color: 'brand-gold' });

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: tagsService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: tagsService.create,
    onSuccess: () => {
      toast({ title: 'Tag criada com sucesso!', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setIsCreating(false);
      setFormData({ name: '', color: 'brand-gold' });
    },
    onError: () => {
      toast({ title: 'Erro ao criar tag', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      tagsService.update(id, data),
    onSuccess: () => {
      toast({ title: 'Tag atualizada!', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setEditingId(null);
      setFormData({ name: '', color: 'brand-gold' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar tag', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tagsService.delete,
    onSuccess: () => {
      toast({ title: 'Tag removida!', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: () => {
      toast({ title: 'Erro ao remover tag', variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (tag: any) => {
    setFormData({ name: tag.name, color: tag.color || 'brand-gold' });
    setEditingId(tag.id);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setFormData({ name: '', color: 'brand-gold' });
    setEditingId(null);
    setIsCreating(false);
  };

  const colorOptions = [
    { value: 'brand-gold', label: 'Dourado', className: 'bg-brand-gold' },
    { value: 'brand-cigar', label: 'Charuto', className: 'bg-brand-cigar' },
    { value: 'brand-ink', label: 'Ink', className: 'bg-brand-ink' },
    { value: 'green-500', label: 'Verde', className: 'bg-green-500' },
    { value: 'blue-500', label: 'Azul', className: 'bg-blue-500' },
    { value: 'red-500', label: 'Vermelho', className: 'bg-red-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Tags</h1>
          <p className="text-brand-ivory/60 mt-1">Organize seus clientes com tags</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => {
              setIsCreating(true);
              setEditingId(null);
            }}
            variant="premium"
            disabled={!isAdmin}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Tag
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingId) && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">
                {editingId ? 'Editar Tag' : 'Nova Tag'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-brand-ivory">
                      Nome
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="color" className="text-brand-ivory">
                      Cor
                    </Label>
                    <div className="flex gap-2">
                      {colorOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, color: option.value })
                          }
                          className={`w-8 h-8 rounded-full ${option.className} ${
                            formData.color === option.value
                              ? 'ring-2 ring-brand-gold ring-offset-2 ring-offset-brand-ink'
                              : ''
                          }`}
                          title={option.label}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" variant="premium">
                    {editingId ? 'Atualizar' : 'Criar'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tags List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tags.map((tag: any) => (
            <motion.div
              key={tag.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
                <CardContent className="p-4" onClick={() => isAdmin && handleEdit(tag)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TagIcon
                        className={`w-5 h-5 text-${tag.color || 'brand-gold'}`}
                      />
                      <div>
                        <h3 className="font-medium text-brand-ivory">
                          {tag.name}
                        </h3>
                        <p className="text-xs text-brand-ivory/50">
                          {tag._count?.clients || 0} clientes
                        </p>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(tag)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMutation.mutate(tag.id)}
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {tags.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <TagIcon className="w-12 h-12 mx-auto text-brand-gold/30 mb-4" />
          <p className="text-brand-ivory/50">Nenhuma tag criada ainda</p>
          {isAdmin && (
            <Button
              onClick={() => setIsCreating(true)}
              variant="outline"
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar primeira tag
            </Button>
          )}
        </div>
      )}
    </div>
  );
}